import React, { useState, useEffect } from "react";
import authService from "../services/discordAuth";
import { trackEvent } from "../utils/analytics";
import { useTranslation, Trans } from "react-i18next";
import "./VoteModal.css";

function VoteModal({ isOpen, onClose, bot, onVoteSuccess }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1); // 1: Verificação, 2: Captcha, 3: Sucesso
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [captchaQuestion, setCaptchaQuestion] = useState({
    num1: 0,
    num2: 0,
    answer: 0,
  });
  const [error, setError] = useState("");
  const [timeRemaining, setTimeRemaining] = useState(28800); // 8 horas em segundos
  const [isVoting, setIsVoting] = useState(false); // Prevenir cliques duplos

  // Bloquear scroll quando modal abrir
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, parseInt(scrollY || "0") * -1);
    }

    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
    };
  }, [isOpen]);

  // Gerar captcha simples
  useEffect(() => {
    if (isOpen && step === 2) {
      const n1 = Math.floor(Math.random() * 10) + 1;
      const n2 = Math.floor(Math.random() * 10) + 1;
      setCaptchaQuestion({
        num1: n1,
        num2: n2,
        answer: n1 + n2,
      });
    }
  }, [isOpen, step]);

  // Timer countdown
  useEffect(() => {
    if (step === 3) {
      const interval = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 0) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [step]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleClose = () => {
    setStep(1);
    setCaptchaAnswer("");
    setError("");
    setIsVoting(false);
    onClose();
  };

  const handleVerify = () => {
    setStep(2);
  };

  const handleVote = async () => {
    if (parseInt(captchaAnswer) !== captchaQuestion.answer) {
      setError(t("voteModal.errorWrongAnswer"));
      setCaptchaAnswer("");
      return;
    }

    if (isVoting) return;

    setIsVoting(true);

    try {
      const token = authService.getAccessToken();
      if (!token) {
        setError(t("voteModal.errorLoginRequired"));
        setIsVoting(false);
        return;
      }

      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
      const response = await fetch(`${BACKEND_URL}/api/bots/${bot.id}/vote`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        trackEvent("bot_vote", {
          bot_id: bot.id,
          bot_name: bot.name,
        });
        setError("");
        setStep(3);
        if (onVoteSuccess) {
          onVoteSuccess();
        }
      } else {
        if (data.cooldown) {
          setError(data.error);
          setTimeRemaining(data.remainingSeconds || 28800);
        } else {
          setError(data.error || t("voteModal.errorGeneric"));
        }
        setIsVoting(false);
      }
    } catch (err) {
      console.error("Erro ao votar:", err);
      setError(t("voteModal.errorConnection"));
      setIsVoting(false);
    }
  };

  if (!isOpen || !bot) return null;

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-content vote-modal">
        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">
            <span>🗳️</span> {t("voteModal.title", { name: bot.name })}
          </h2>
          <button className="modal-close" onClick={handleClose}>
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Step 1: Verificação */}
          {step === 1 && (
            <div className="vote-step">
              <div className="vote-bot-info">
                <div className="vote-bot-avatar">{bot.avatar?.startsWith("http") ? <img src={bot.avatar} alt={bot.name} /> : bot.avatar}</div>
                <div>
                  <h3 className="vote-bot-name">{bot.name}</h3>
                  <p className="vote-bot-tagline">{bot.tagline}</p>
                </div>
              </div>

              <div className="vote-info-box">
                <h4>{t("voteModal.infoTitle")}</h4>
                <ul>
                  <li>{t("voteModal.rule1")}</li>
                  <li>{t("voteModal.rule2")}</li>
                  <li>{t("voteModal.rule3")}</li>
                </ul>
              </div>

              <div className="vote-stats">
                <div className="vote-stat-item">
                  <span className="vote-stat-label">{t("voteModal.totalVotes")}</span>
                  <span className="vote-stat-value">{bot.votes}</span>
                </div>
                <div className="vote-stat-item">
                  <span className="vote-stat-label">{t("voteModal.ranking")}</span>
                  <span className="vote-stat-value">#{bot.rank || "?"}</span>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Captcha */}
          {step === 2 && (
            <div className="vote-step">
              <div className="captcha-box">
                <h4>{t("voteModal.securityTitle")}</h4>
                <p>{t("voteModal.securityDesc")}</p>

                <div className="captcha-question">
                  {t("voteModal.captchaQuestion", {
                    num1: captchaQuestion.num1,
                    num2: captchaQuestion.num2,
                  })}
                </div>

                <input
                  type="number"
                  className="captcha-input"
                  placeholder={t("voteModal.inputPlaceholder")}
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !isVoting) {
                      e.preventDefault();
                      handleVote();
                    }
                  }}
                  autoFocus
                />

                {error && <div className="error-message">❌ {error}</div>}
              </div>
            </div>
          )}

          {/* Step 3: Sucesso */}
          {step === 3 && (
            <div className="vote-step vote-success">
              <div className="success-icon">✅</div>
              <h3>{t("voteModal.successTitle")}</h3>
              <p>
                <Trans i18nKey="voteModal.successMessage" values={{ name: bot.name }} defaults="Obrigado por votar em <0>{{name}}</0>!" components={{ 0: <strong /> }} />
              </p>

              <div className="cooldown-info">
                <p>{t("voteModal.cooldownLabel")}</p>
                <div className="cooldown-timer">{formatTime(timeRemaining)}</div>
              </div>

              <div className="share-section">
                <p>{t("voteModal.shareTitle")}</p>
                <div className="share-buttons">
                  <button className="share-btn twitter">{t("voteModal.shareTwitter")}</button>
                  <button className="share-btn discord">{t("voteModal.shareDiscord")}</button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          {step === 1 && (
            <>
              <button className="btn btn-danger" onClick={handleClose}>
                {t("voteModal.btnCancel")}
              </button>
              <button className="btn btn-primary" onClick={handleVerify}>
                {t("voteModal.btnContinue")}
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <button className="btn btn-outline" onClick={() => setStep(1)}>
                {t("voteModal.btnBack")}
              </button>
              <button className="btn btn-primary" onClick={handleVote} disabled={isVoting}>
                {isVoting ? t("voteModal.btnVoting") : t("voteModal.btnConfirm")}
              </button>
            </>
          )}

          {step === 3 && (
            <button className="btn btn-primary btn-block" onClick={handleClose}>
              {t("voteModal.btnClose")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default VoteModal;
